import { ITEM_TABLE_TYPE, SCALING_TABLE_TYPE } from '@/main/app.constants';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { OperatorEnum } from '@/sdk/model/PropertyFilterInputV1';

export const SEARCH_PER_PAGE = 35;
export const ASSET_TREE_SEARCH_LIMIT = 1000;
export const MAX_RETURNED_RESULTS = 1000;
export const MAX_SWAP_OPTION_NAME_LENGTH = 50;
export type SearchPanes = 'main' | 'modal';
export type SearchModes =
  | 'overview'
  | 'search'
  | 'tree'
  | 'pinned'
  | 'recent'
  | 'asset-groups'
  | 'usages'
  | 'scaling-tables'
  | 'item-tables'
  | 'scaling-tables-tree'
  | 'item-hierarchies'
  | 'hierarchy-nodes'
  | 'models';

export const SORT_BY_OPTIONS = {
  DEFAULT: '',
  NAME: 'Name',
  DESCRIPTION: 'Description',
};
export const SEARCH_BREADCRUMB = {
  name: 'SEARCH_DATA.SEARCH_RESULTS',
  id: 'SEARCH',
  type: 'VIEW_MODE',
};
export const HOME_BREADCRUMB = { id: '' };
export const SEARCH_USAGES_BREADCRUMB = {
  name: 'SEARCH_DATA.USAGES',
  id: 'ITEM_USAGES',
  type: 'VIEW_MODE',
};
export const SEARCH_RESULT_TYPES = {
  ITEMS: 'items',
  ASSETS: 'assets',
} as const;

export const DATASOURCES_TO_EXCLUDE = ['Auth', 'LDAP'];

export const OPERATOR_OPTIONS = [
  { value: OperatorEnum.EQUALS, text: 'EQUALS' },
  { value: OperatorEnum.EQUALSIGNORECASE, text: 'EQUALS_IGNORE_CASE' },
  { value: OperatorEnum.NOTEQUALS, text: 'NOT_EQUALS' },
  { value: OperatorEnum.WITHIN, text: 'ITEM_FINDER.PROPERTY_OPERATORS.WITHIN' },
  { value: OperatorEnum.STRINGCONTAINS, text: 'ITEM_FINDER.PROPERTY_OPERATORS.STRING_CONTAINS' },
  { value: OperatorEnum.NOTSTRINGCONTAINS, text: 'ITEM_FINDER.PROPERTY_OPERATORS.NOT_STRING_CONTAINS' },
  { value: OperatorEnum.REGEXMATCHES, text: 'ITEM_FINDER.PROPERTY_OPERATORS.REGEX_MATCHES' },
  { value: OperatorEnum.NOTREGEXMATCHES, text: 'ITEM_FINDER.PROPERTY_OPERATORS.NOT_REGEX_MATCHES' },
];

export const PARENT_PROPERTY = 'parent';
export const ASSET_NAME_PROPERTY = 'assetName';
export const ASSET_FULL_PATH_PROPERTY = 'assetFullPath';
export const ASSET_RELATED_PROPERTIES = [ASSET_NAME_PROPERTY, ASSET_FULL_PATH_PROPERTY];

export interface SearchPropertyOption {
  value: string;
  text: string;
}

export const BASE_SEARCH_PROPERTY_OPTIONS = [
  { value: SeeqNames.Properties.Name, text: 'ITEM_FINDER.PROPERTY_SELECT_NAME' },
  { value: SeeqNames.Properties.Description, text: 'ITEM_FINDER.PROPERTY_SELECT_DESCRIPTION' },
  { value: SeeqNames.Properties.DatasourceClass, text: 'ITEM_FINDER.PROPERTY_SELECT_DATASOURCE' },
];

export const DEFAULT_SEARCH_PROPERTY_OPTIONS: SearchPropertyOption[] = [...BASE_SEARCH_PROPERTY_OPTIONS];

export const ITEM_SEARCH_PROPERTY_OPTIONS = [
  ...BASE_SEARCH_PROPERTY_OPTIONS,
  { value: PARENT_PROPERTY, text: 'ITEM_SEARCH.PROPERTY.PARENT' },
];

export const ITEM_SEARCH_TYPE_OPTIONS = [
  { value: SeeqNames.Types.Asset, text: 'ITEM_TYPES.ASSET' },
  { value: SeeqNames.Types.Condition, text: 'ITEM_TYPES.CONDITION' },
  { value: SeeqNames.Types.Signal, text: 'ITEM_TYPES.SIGNAL' },
  { value: SeeqNames.Types.Scalar, text: 'ITEM_TYPES.SCALAR_ITEM' },
];

export const DESCENDANT_SEARCH_TYPE_OPTIONS = [
  { value: SeeqNames.Types.Condition, text: 'ITEM_TYPES.CONDITION' },
  { value: SeeqNames.Types.Signal, text: 'ITEM_TYPES.SIGNAL' },
  { value: SeeqNames.Types.Scalar, text: 'ITEM_TYPES.SCALAR_ITEM' },
];

export const FIND_ITEMS_EXTRA_SEARCH_PROPERTY_OPTIONS = [
  ...ITEM_SEARCH_PROPERTY_OPTIONS,
  { value: ASSET_NAME_PROPERTY, text: 'ITEM_SEARCH.PROPERTY.ASSET_NAME' },
  { value: ASSET_FULL_PATH_PROPERTY, text: 'ITEM_SEARCH.PROPERTY.ASSET_FULL_PATH' },
];

export const DESCENDANT_SEARCH_PROPERTY_OPTIONS = [
  { value: SeeqNames.Properties.Name, text: 'ITEM_FINDER.PROPERTY_SELECT_NAME' },
  { value: SeeqNames.Properties.Id, text: 'SCALING.RULES.DESCENDANT_SEARCH.PROPERTY.ID' },
  { value: SeeqNames.Properties.Alias, text: 'SCALING.RULES.DESCENDANT_SEARCH.PROPERTY.ALIAS' },
];

export const FIND_DESCENDANT_PROPERTY_OPTIONS = [
  ...BASE_SEARCH_PROPERTY_OPTIONS,
  { value: SeeqNames.Properties.Alias, text: 'SCALING.RULES.DESCENDANT_SEARCH.PROPERTY.ALIAS' },
];

export const FIND_DESCENDANT_TYPE_OPTIONS = [
  { value: SeeqNames.Types.Condition, text: 'ITEM_TYPES.CONDITION' },
  { value: SeeqNames.Types.Signal, text: 'ITEM_TYPES.SIGNAL' },
  { value: SeeqNames.Types.Scalar, text: 'ITEM_TYPES.SCALAR_ITEM' },
  { value: SeeqNames.Types.Asset, text: 'ITEM_TYPES.ASSET' },
];

export const PROPERTY_TYPE_OPTIONS = [
  { value: 'STRING', text: 'SCALING.RULES.FIND_DESCENDANT.PROPERTY_TYPE.TEXT' },
  { value: 'NUMBER', text: 'SCALING.RULES.FIND_DESCENDANT.PROPERTY_TYPE.NUMBER' },
  { value: 'BOOLEAN', text: 'SCALING.RULES.FIND_DESCENDANT.PROPERTY_TYPE.BOOLEAN' },
];

export const HierarchyNodeType = 'HierarchyNode';
export const ItemTableNameProperty = 'Table Name';

export const TABLE_DEFINITION_SUBTYPES = [SCALING_TABLE_TYPE, ITEM_TABLE_TYPE];

/*
 * Limit of items shown in the overview mode of the Search Widget
 */
export const LIMIT_IN_OVERVIEW = 8;
